import { useActivityFormStore } from "PFApp/activities/activity_form/activity_form_store";
import { useSubtemplate } from "PFApp/activities/activity_form/hooks/use_subtemplate";
import { propertyTypeToComponentMap as commonPropertyTypeToComponentMap } from "PFApp/components/activity_form/field_factory/property_type_to_component_map";
import { FIELD_CUSTOM_FIELDS_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { useConnectedFields } from "PFApp/hooks/activity_form/use_connected_fields";
import { propertyToTitle } from "PFCore/helpers/templates";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { ParsedSchemaProperty } from "PFTypes";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";

import { CustomValuesEditFieldWrapper } from "./field_wrappers/custom_values_edit_field_wrapper";

const propertyTypeToComponentMap = {
  ...commonPropertyTypeToComponentMap,
  custom_field: CustomValuesEditFieldWrapper
};

type FieldFactoryProps = {
  property: ParsedSchemaProperty;
  value?: any;
  error?: string;
  onChange?: (value: any) => void;
  required?: boolean;
};

export const FieldFactory = ({ property, value, error, required, onChange }: FieldFactoryProps) => {
  const Component = propertyTypeToComponentMap[property.type] || false;
  const isText = property.type === "text";
  const isCustomField = property.type === "custom_field";
  const { customTypes } = useCustomTypes();
  const { data: currentProfile } = useCurrentProfile();
  const template = useActivityFormStore((state) => state.template);
  const qaIdPrefix = useActivityFormStore((state) => state.qaIdPrefix);

  const subtemplate = useSubtemplate();
  const customFields = useWatch({ name: FIELD_CUSTOM_FIELDS_KEY });
  const connectedValuesIds = useConnectedFields({ property, template, subtemplate, customFields });

  const additionalProps = useMemo(() => {
    if (!isCustomField) {
      return isText ? { inputType: "textarea", minHeight: 40, controlledValue: true } : undefined;
    }

    const customType = customTypes.find((type) => type.name === property.name);

    return customType ? { currentProfile, customType, qaIdPrefix } : {};
  }, [isCustomField, customTypes, currentProfile, qaIdPrefix, isText, property.name]);

  const style = useMemo(() => ({ width: property?.width || "100%", display: "block" }), [property]);
  const label = propertyToTitle(template, property.name, customTypes) || "";

  return (
    Component && (
      <div style={style}>
        <Component
          property={property}
          connectedValuesIds={connectedValuesIds}
          onChange={onChange}
          maxLength={property.max_length}
          error={error}
          label={label}
          value={value}
          qaId={`${qaIdPrefix}-${property.name}`}
          required={required}
          {...additionalProps}
        />
      </div>
    )
  );
};
